import { serverSideTranslations } from "next-i18next/serverSideTranslations";

import AnlassLayout from "@/components/anlass/AnlassLayout";
import HochzeitInhalt from "@/components/anlass/HochzeitInhalt";
import { loadCatalog } from "@/lib/catalog";
import { buildAnlassBoxen } from "@/utils/buildPopularBoxes";
import { anlassAnalyse, ohneBoxen } from "@/utils/anlassAnalyse";
import { hochzeitFaq } from "@/utils/hochzeitFaq";
import { euro } from "@/utils/preisAnalyse";

/**
 * Die Hochzeitsseite als eigene Seite statt als Abzug der Stadtseiten-Vorlage.
 *
 * Ausgangslage: In 90 Tagen 138 Impressionen und kein einziger Klick —
 * während die Startseite für Hochzeits-Anfragen 21.566 Impressionen
 * einsammelte (Position 17,3). Google hielt die spezialisierte Seite für die
 * schwächere, und das zu Recht: Sie war dieselbe Vorlage wie 190 Stadtseiten,
 * mit ausgetauschten Wörtern.
 *
 * Diese Seite beantwortet stattdessen, was nur bei einer Hochzeit gefragt
 * ist. Die Route liegt als eigene Datei und geht damit der dynamischen
 * [locationID]-Route vor; der Eintrag in locations.json bleibt für Sitemap
 * und Verlinkung bestehen.
 */

const GAESTE = 100;

export async function getStaticProps({ locale }) {
  const json = await loadCatalog();
  const boxen = json.Photobooths || [];

  // Ohne Drucker gibt es nichts fürs Gästebuch — auf dieser Seite zählen nur
  // Pakete, die drucken und für die Gästezahl genug Papier haben.
  const analyse = anlassAnalyse(boxen, { gaeste: GAESTE, druckNoetig: true });
  const popularBoxes = buildAnlassBoxen(analyse?.boxen || [], 6);

  const stand = new Date().toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });

  return {
    props: {
      analyse: ohneBoxen(analyse),
      popularBoxes,
      stand,
      ...(await serverSideTranslations(locale, ["common"])),
    },
// Frist von einer Stunde auf einen Tag (Absprache 2026-10-03):
// Jede Neuerzeugung schreibt die Seite in Vercels Zwischenspeicher, und
// abgerechnet wird in Blöcken zu 8 KB. Eine Seite von ~300 KB kostet so
// rund 36 Einheiten — pro Erzeugung. Stündlich erneuert reicht eine
// Handvoll gecrawlter Seiten, um das Monatskontingent von 200.000
// aufzubrauchen; am 2026-10-03 waren 75 % verbraucht, und bei 100 %
// schaltet Vercel das Projekt ab.
//
// Inhaltlich kostet es nichts: Was sich hier ändert, hängt am Tagesstand
// (daySeed, stand) und wechselt ohnehin nur einmal am Tag.
    revalidate: 86400,
  };
}

export default function HochzeitSeite({ analyse, popularBoxes, stand }) {
  const abPreis = analyse?.min || 0;

  return (
    <AnlassLayout
      seo={{
        titel: abPreis
          ? `Fotobox für die Hochzeit mieten ab ${euro(abPreis)} € – im Vergleich`
          : "Fotobox für die Hochzeit mieten – Preise & Anbieter im Vergleich",
        beschreibung: analyse
          ? `Fotobox für die Hochzeit mieten: ${analyse.anzahlPakete} Pakete mit genug Ausdrucken für ${analyse.gaeste} Gäste, ab ${euro(analyse.min)} €. Mit Druck-Flatrate, eigenem Layout und Online-Galerie — jetzt vergleichen.`
          : "Fotobox für die Hochzeit mieten: Pakete, Preise und Anbieter im Vergleich.",
        pfad: "/fotobox-mieten-hochzeit",
      }}
      breadcrumb="Fotobox für die Hochzeit"
      hero={{
        titel: "Fotobox für die Hochzeit mieten",
        lead: "Nur Fotoboxen, die einen Hochzeitsabend lang durchdrucken — vergleichen und direkt buchen.",
        anzahlBoxen: analyse?.anzahlBoxen || 0,
        anzahlVersand: analyse?.perVersand || 0,
        anzahlRegional: analyse?.bauenAuf || 0,
        abPreis,
        hinweis: (
          <>
            {abPreis > 0 && (
              <>
                Ab{" "}
                <strong className="font-semibold text-white">
                  {euro(abPreis)} €
                </strong>
                .{" "}
              </>
            )}
            Umbuchung bei den meisten Anbietern kostenlos, falls sich euer
            Termin verschiebt.
          </>
        ),
      }}
      angebote={{
        titel: "Fotoboxen, die für eine Hochzeit genug drucken",
        untertitel: analyse
          ? `Ausgewählt aus den Angeboten, die ${analyse.bedarf} Ausdrucke oder eine Druck-Flatrate mitbringen.`
          : "Ausgewählt nach Druckleistung und Ausstattung.",
        items: popularBoxes,
      }}
      faq={hochzeitFaq(analyse)}
      faqTitel="Häufige Fragen zur Fotobox auf der Hochzeit"
      faqUntertitel="Die Fragen, die uns aus der Hochzeitsplanung am häufigsten erreichen."
      staedteTitel="Fotobox für die Hochzeit mieten in deiner Stadt"
      abschlussTitel="Jetzt die passende Fotobox für eure Hochzeit finden"
    >
      <HochzeitInhalt analyse={analyse} stand={stand} />
    </AnlassLayout>
  );
}
